import { useEffect, useState } from "react";
import { useParams } from "wouter";
import type { Workflow } from "@/types/aiToolkit";
import { aiWorkflowsService } from "@/services/aiWorkflowsService";
import { WorkflowCanvas } from "@/components/workflow-canvas/WorkflowCanvas";

/**
 * Phase 4A (experimental) — visual workflow canvas.
 * Routes:
 *   /workflow-canvas        → blank canvas (preview mode, no save target)
 *   /workflow-canvas/:id    → loads an existing ai_workflows record, saves back
 *
 * Additive/opt-in: does not replace the live WorkflowBuilder. Safe to ship dark;
 * only reachable by typing the URL until the team decides to link it.
 */
export default function WorkflowCanvasPage() {
  const params = useParams<{ id?: string }>();
  const id = params.id;
  const [workflow, setWorkflow] = useState<Workflow | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(!!id);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!id) { setLoading(false); return; }
    setLoading(true);
    aiWorkflowsService
      .getWorkflowById(id)
      .then((wf) => {
        if (cancelled) return;
        if (!wf) setNotFound(true);
        else setWorkflow(wf);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id]);

  const onSave = id
    ? async (patch: Partial<Workflow>) => {
        await aiWorkflowsService.updateWorkflow(id, {
          nodes: patch.nodes,
          edges: patch.edges,
          entryNodeId: patch.entryNodeId,
        } as Partial<Workflow>);
      }
    : undefined;

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border, #e2e8f0)", display: "flex", alignItems: "center", gap: 10 }}>
        <strong>Workflow Canvas</strong>
        <span style={{ fontSize: 12, opacity: 0.6 }}>
          experimental · React Flow over the existing engine {id ? `· editing ${id.slice(0, 8)}…` : "· preview"}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <div style={{ padding: 24 }}>Loading workflow…</div>
        ) : notFound ? (
          <div style={{ padding: 24 }}>Workflow not found (or not yours).</div>
        ) : (
          <WorkflowCanvas workflow={workflow} onSave={onSave} />
        )}
      </div>
    </div>
  );
}
